/**
 * 2026 Shell renderer.
 *
 * Each page provides:
 *   <body data-active="dashboard" data-crumbs="Workspace | Dashboard">
 *     <div class="shell">
 *       <div data-shell-sidebar></div>
 *       <div class="main">
 *         <div data-shell-topbar></div>
 *         <main class="content"> ...page content... </main>
 *         <div data-shell-footer></div>
 *       </div>
 *     </div>
 *   </body>
 *
 * mountShell() fills the three placeholder divs with the shared chrome,
 * marking the active sidebar item and writing the breadcrumbs.
 *
 * NAV is the single source of truth — adding a page is one entry here.
 */

export const NAV = [
  {
    label: 'Traffic WiFi',
    items: [
      { key: 'wifi', text: 'Report Dashboard', href: 'index.html',
        icon: '<path d="M3 20h18"/><path d="M7 20V9"/><path d="M12 20V4"/><path d="M17 20v-7"/>' },
    ],
  },
  {
    label: 'Report Pages',
    items: [
      { key: 'nat-shortcut', text: 'NAT_ALL', href: 'index.html?page=nat',
        icon: '<path d="M4 17a8 8 0 0 1 16 0"/><path d="M12 17l4-7"/>' },
      { key: 'bng-shortcut', text: 'BNG_ALL', href: 'index.html?page=bng',
        icon: '<path d="M4 7h16"/><path d="M4 12h16"/><path d="M4 17h16"/>' },
      { key: 'bras-shortcut', text: 'BRAS_ALL', href: 'index.html?page=bras',
        icon: '<path d="M5 7h14v10H5z"/><path d="M9 11h6"/>' },
      { key: 'trend-shortcut', text: 'Trends', href: 'index.html?page=trendPct',
        icon: '<path d="m4 16 5-5 4 4 7-8"/><path d="M20 7v6h-6"/>' },
    ],
  },
];

const BRAND_LOGO = `<svg viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg">
  <path fill="#ffffff" d="M8 24h4v4H8zM16 18h4v10h-4zM24 11h4v17h-4z"/>
  <path fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round" d="M7 13c6-6 16-6 22 0M11 17c4-3.5 10-3.5 14 0"/>
</svg>`;

const SIDEBAR_TOGGLE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9">
  <path d="M15 18l-6-6 6-6"/>
</svg>`;

const CHEV = '<svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="m9 18 6-6-6-6"/></svg>';

function renderNavLink(item, activeKey) {
  const active = item.key === activeKey ? ' is-active' : '';
  const badge = item.badge
    ? `<span class="nav-badge ${item.badge.kind}">${item.badge.text}</span>`
    : '';
  return `
    <a class="nav-link${active}" href="${item.href}">
      <svg viewBox="0 0 24 24">${item.icon}</svg>
      <span>${item.text}</span>
      ${badge}
    </a>`;
}

function renderNavGroup(item, activeKey) {
  const open = item.children.some((c) => c.key === activeKey) ? ' is-open' : '';
  const submenu = item.children
    .map((c) => `<a href="${c.href}">${c.text}</a>`)
    .join('');
  return `
    <div class="nav-item-group${open}" data-nav-group>
      <a class="nav-link" href="javascript:void(0)" data-nav-toggle>
        <svg viewBox="0 0 24 24">${item.icon}</svg>
        <span>${item.text}</span>
        ${CHEV}
      </a>
      <div class="nav-submenu">${submenu}</div>
    </div>`;
}

function renderSection(section, activeKey) {
  const items = section.items.map((item) => (
    item.children ? renderNavGroup(item, activeKey) : renderNavLink(item, activeKey)
  )).join('');
  return `
    <nav class="nav-section">
      <div class="nav-label">${section.label}</div>
      ${items}
    </nav>`;
}

function renderSidebar(activeKey) {
  const sections = NAV.map((s) => renderSection(s, activeKey)).join('');
  return `
    <aside class="d-sidebar">
      <div class="brand">
        <div class="brand-logo">${BRAND_LOGO}</div>
        <div class="brand-text">
          <div class="brand-name">WiFi Report</div>
          <div class="brand-tag">Traffic v1.2</div>
        </div>
        <button class="sidebar-toggle" data-sidebar-toggle aria-label="Collapse sidebar" title="Collapse sidebar">
          ${SIDEBAR_TOGGLE_ICON}
        </button>
      </div>
      ${sections}
      <div class="sidebar-footer">
        <div class="workspace">
            <div class="workspace-avatar">BI</div>
            <div class="workspace-text">
            <div class="workspace-name">Network BI</div>
            <div class="workspace-role">internal report</div>
          </div>
          <svg class="workspace-chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <path d="m7 9 5-5 5 5"/><path d="m7 15 5 5 5-5"/>
          </svg>
        </div>
      </div>
    </aside>`;
}

function renderCrumbs(crumbsAttr) {
  if (!crumbsAttr) return '';
  const parts = crumbsAttr.split('|').map((p) => p.trim()).filter(Boolean);
  const sep = '<svg class="sep" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>';
  return parts.map((p, i) => {
    const cls = i === parts.length - 1 ? ' class="current"' : '';
    return `${i > 0 ? sep : ''}<span${cls}>${p}</span>`;
  }).join('');
}

function renderTopbar(crumbsAttr) {
  return `
    <header class="d-topbar report-topbar">
      <div class="crumbs">
        <button class="hamburger" data-drawer-open aria-label="Open navigation">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
        </button>
        ${renderCrumbs(crumbsAttr)}
      </div>
      <div class="topbar-actions">
        <div class="report-status"><span></span>LAN 8082</div>
        <a class="report-host" href="http://192.168.0.152:8082">192.168.0.152</a>
        <button class="icon-btn" id="themeToggle" aria-label="Toggle theme"></button>
        <div class="avatar" aria-label="Network BI">BI</div>
      </div>
    </header>`;
}

function renderFooter() {
  return `
    <footer class="d-footer">
      <div>Traffic WiFi Report v1.2 · Adminator shell by <a href="https://colorlib.com" target="_blank" rel="nofollow noopener noreferrer">Colorlib</a></div>
      <div class="d-footer-meta">
        <span>static build</span>
        <span>internal</span>
      </div>
    </footer>`;
}

export function mountShell() {
  const body = document.body;
  const activeKey = body.getAttribute('data-active') || '';
  const crumbs = body.getAttribute('data-crumbs') || '';

  const sidebarHost = document.querySelector('[data-shell-sidebar]');
  const topbarHost  = document.querySelector('[data-shell-topbar]');
  const footerHost  = document.querySelector('[data-shell-footer]');

  if (sidebarHost) sidebarHost.outerHTML = renderSidebar(activeKey);
  if (topbarHost)  topbarHost.outerHTML  = renderTopbar(crumbs);
  if (footerHost)  footerHost.outerHTML  = renderFooter();
}
